<template>
  <v-container fluid>
    <page-title icon="fas fa-fw fa-users">
      {{ group.name }}
      <template v-slot:buttons>
        <v-btn :to="{name: 'admin.group.edit', params: {groupID: group.id}}"
               color="primary">
          Edit
        </v-btn>
      </template>
    </page-title>

    <v-card class="mb-4">
      <v-card-title>
        Has {{ group.users.length | pluralize('member') }}:
      </v-card-title>
      <v-simple-table v-if="group.users.length > 0">
        <thead>
        <tr>
          <th>
            Name
          </th>
          <th>
            Email
          </th>
          <th>
            Status
          </th>
          <th>
            Actions
          </th>
        </tr>
        </thead>
        <tbody>
        <template>
          <tr v-for="u in sortedUsers"
              :key="u.id">
            <td>
              {{ u.name }}
            </td>
            <td>
              {{ u.email }}
            </td>
            <td>
              <v-chip v-if="u.status === 'Active'"
                      color="green"
                      label
                      text-color="white"
                      small>
                {{ u.status }}
              </v-chip>
              <v-chip v-else
                      color="red"
                      label
                      text-color="white"
                      small>
                Inactive
              </v-chip>
            </td>
            <td>
              <v-btn @click="removeUserFromGroup(u)"
                     x-small
                     color="error"
                     :disabled="group.is_all_users && team.all_users_group_enabled">
                <v-icon left
                        x-small>
                  fal fa-fw fa-pencil
                </v-icon>
                Remove
              </v-btn>
            </td>
          </tr>
        </template>
        </tbody>
      </v-simple-table>
    </v-card>
    <v-card class="mb-4">
      <v-card-title>
        Has access to {{ group.presentations.length | pluralize('presentation') }}:
      </v-card-title>
      <v-simple-table v-if="group.presentations.length > 0">
        <thead>
        <tr>
          <th>
            Name
          </th>
          <th>
            Status
          </th>
          <th>
            Actions
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="pr in sortedPresentations"
            :key="pr.id">
          <td>
            {{ pr.name }}
          </td>

          <td>
            <v-chip v-if="pr.is_active"
                    color="green"
                    label
                    text-color="white"
                    small>
              {{ pr.status }}

            </v-chip>

            <v-chip v-else
                    color="red"
                    label
                    text-color="white"
                    small>
              Inactive
            </v-chip>
          </td>
          <td>
            <v-btn @click="removePresentationFromGroup(pr)"
                   x-small
                   color="error">
              <v-icon left
                      x-small>
                fal fa-fw fa-pencil
              </v-icon>
              Remove
            </v-btn>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card>
    <v-card class="mb-4">
      <v-card-title>
        Has access to {{ group.templates.length | pluralize('email template') }}:
      </v-card-title>
      <v-simple-table v-if="group.templates.length > 0">
        <thead>
        <tr>
          <th>
            Name
          </th>
          <th>
            Status
          </th>
          <th>
            Actions
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="template in sortedShareTemplates"
            :key="template.id">
          <td>
            {{ template.name }}
          </td>

          <td>
            <v-chip v-if="template.is_active"
                    color="green"
                    label
                    text-color="white"
                    small>
              {{ template.status }}

            </v-chip>

            <v-chip v-else
                    color="red"
                    label
                    text-color="white"
                    small>
              Inactive
            </v-chip>
          </td>
          <td>
            <v-btn @click="removeTemplateFromGroup(template)"
                   x-small
                   color="error">
              <v-icon left
                      x-small>
                fal fa-fw fa-pencil
              </v-icon>
              Remove
            </v-btn>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card>
  </v-container>
</template>


<script>
import {mapGetters} from 'vuex';
import DOMPurify from 'dompurify';
import filters from '../../../../ml/mixins/filters';
import {orderBy} from 'lodash';
import PageTitle from '../../components/PageTitle.vue';

export default {
  components: {PageTitle},
  metaInfo() {
    this.$store.commit('setTitle', `Group: ${this.group.name}`);
    return {
      title: this.$store.getters.title,
    }
  },
  name: 'show-group',
  mixins: [filters],
  props: {
    group: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      breadcrumbs: [
        {text: 'Groups', disabled: false, exact: true, to: {name: 'groups'}},
        {text: 'List'}
      ],
      busy: false,
    }
  },
  computed: {
    ...mapGetters([
      'shareTemplates',
      'activeUsers',
      'groups',
      'presentations',
      'team',
      'users',
    ]),
    sortedPresentations() {
      return orderBy(this.group.presentations, ['slug', 'id'], ['asc', 'asc']);
    },
    sortedUsers() {
      return orderBy(this.group.users, ['slug', 'id'], ['asc', 'asc']);
    },
    sortedShareTemplates() {
      return orderBy(this.group.templates, ['slug', 'id'], ['asc', 'asc']);
    },
  },
  created() {
  },
  methods: {
    removePresentationFromGroup(presentation) {
      const group = this.group;
      axios.delete(`/api/groups/${group.id}/presentations/${presentation.id}`)
          .then(response => {
            this.$toast.success(`${DOMPurify.sanitize(group.name)} cannot access ${DOMPurify.sanitize(presentation.name)} anymore.`);
            this.$store.dispatch('fetchGroups');
            this.$store.dispatch('fetchPresentations');
            this.$emit('updated');
          })
          .catch(err => {
            this.$toast.error(DOMPurify.sanitize(err.response.data.error));
          })
    },
    removeTemplateFromGroup(template) {
      const group = this.group;
      axios.delete(`/api/groups/${group.id}/share-templates/${template.id}`)
          .then(response => {
            this.$toast.success(`${DOMPurify.sanitize(group.name)} cannot access ${DOMPurify.sanitize(template.name)} anymore.`);
            this.$store.dispatch('fetchGroups');
            this.$store.dispatch('fetchShareTemplates');
            this.$emit('updated');
          })
          .catch(err => {
            this.$toast.error(DOMPurify.sanitize(err.response.data.error));
          })
    },
    removeUserFromGroup(user) {
      const group = this.group;
      if (group.is_all_users && this.team.all_users_group_enabled) {
        this.$toast.error('You cannot remove users from the all users group.')
        return;
      }
      axios.delete(`/api/groups/${group.id}/users/${user.id}`)
          .then(response => {
            this.$toast.success(`${DOMPurify.sanitize(user.name)} removed from ${DOMPurify.sanitize(group.name)}`);
            this.$store.dispatch('fetchGroups');
            this.$store.dispatch('fetchPresentations');
            this.$emit('updated');
          })
          .catch(err => {
            this.$toast.error(DOMPurify.sanitize(err.response.data.error));
          })
    }
  }
}
</script>
